// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Aggregation.
 *
 * Nothing here keeps a row. A two-million-row file produces two million
 * `RowCount`s and every one of them is folded into fixed-size state and dropped,
 * so peak memory is a function of the chunk size and never of the file size.
 *
 * The two exceptions are deliberate and bounded: the over-limit list, which is
 * capped, and the histogram, which is a fixed number of counters.
 */

import type { Histogram, OverLimitRow, RowCount, RowProblem, Totals } from '../types';
import { ROLES } from '../types';

/**
 * Histogram edges.
 *
 * Logarithmic, because token lengths are: a chat dataset is a dense cluster
 * under 2,000 tokens with a long thin tail, and linear buckets render that as
 * one full bar and thirty empty ones. The top edge is Infinity so nothing falls
 * off the end silently.
 */
export const HISTOGRAM_EDGES: readonly number[] = [
  64, 128, 256, 512, 1_024, 2_048, 4_096, 8_192, 16_384, 32_768, 65_536, 131_072, 262_144, Infinity,
];

/** How many over-limit rows are kept for export. */
export const OVER_LIMIT_CAP = 5_000;

/**
 * Total bytes of over-limit row TEXT held per worker.
 *
 * An over-limit row is by definition a long one — at a 65,536-token limit each is
 * at least ~200 kB — so 5,000 of them held in full would be a gigabyte. The
 * budget is what keeps the work queue useful without reintroducing the whole-file
 * -in-memory problem the streaming design exists to avoid.
 */
export const OVER_LIMIT_BYTES = 24 * 1024 * 1024;

export function emptyProblems(): Record<RowProblem, number> {
  return {
    'not-json': 0,
    'no-messages': 0,
    'empty-messages': 0,
    'message-not-object': 0,
    'content-not-string': 0,
    'no-assistant': 0,
  };
}

export function emptyTotals(): Totals {
  return {
    rows: 0,
    blankLines: 0,
    bytes: 0,
    tokens: 0,
    cappedTokens: 0,
    contentTokens: 0,
    byRole: new Int32Array(ROLES.length),
    messages: 0,
    overLimit: 0,
    truncatedTokens: 0,
    longest: 0,
    shortest: Number.POSITIVE_INFINITY,
    problems: emptyProblems(),
    histogram: { edges: HISTOGRAM_EDGES, counts: new Int32Array(HISTOGRAM_EDGES.length) },
  };
}

function bucketOf(tokens: number): number {
  for (let i = 0; i < HISTOGRAM_EDGES.length; i++) if (tokens <= HISTOGRAM_EDGES[i]) return i;
  return HISTOGRAM_EDGES.length - 1;
}

/**
 * Fold one row in.
 *
 * `limit` is applied here so `overLimit` and `truncatedTokens` are accumulated in
 * the same single pass as everything else — but see `rescore` for why the limit
 * can still be changed afterwards without re-reading the file.
 */
export function accumulate(totals: Totals, row: RowCount, limit: number | null): void {
  totals.bytes += row.bytes;
  if (row.problem !== null) totals.problems[row.problem] += 1;
  if (row.problem === 'not-json' || row.problem === 'no-messages' || row.problem === 'empty-messages') {
    // Counted as a row of the file — it is a line the user has to fix — but it
    // contributes no tokens, so it must not move the length statistics.
    totals.rows += 1;
    return;
  }

  totals.rows += 1;
  totals.tokens += row.tokens;
  // The cap is applied HERE, per row, which is the whole point: a provider
  // truncates each record down to the limit before training, so it bills the
  // lesser of the two. Summing raw counts and capping the total afterwards gives
  // a different — and larger — number on exactly the files that have over-limit
  // rows, which is the case this tool exists to find.
  totals.cappedTokens += limit === null ? row.tokens : Math.min(row.tokens, limit);
  totals.contentTokens += row.contentTokens;
  totals.messages += row.messages;
  for (let i = 0; i < totals.byRole.length; i++) totals.byRole[i] += row.byRole[i];

  if (row.tokens > totals.longest) totals.longest = row.tokens;
  if (row.tokens < totals.shortest) totals.shortest = row.tokens;
  totals.histogram.counts[bucketOf(row.tokens)] += 1;

  if (limit !== null && row.tokens > limit) {
    totals.overLimit += 1;
    totals.truncatedTokens += row.tokens - limit;
  }
}

/** Merge one worker's totals into another's. Every field must be handled or a lane's work vanishes. */
export function merge(into: Totals, from: Totals): void {
  into.rows += from.rows;
  into.blankLines += from.blankLines;
  into.bytes += from.bytes;
  into.tokens += from.tokens;
  into.cappedTokens += from.cappedTokens;
  into.contentTokens += from.contentTokens;
  into.messages += from.messages;
  into.overLimit += from.overLimit;
  into.truncatedTokens += from.truncatedTokens;
  into.longest = Math.max(into.longest, from.longest);
  into.shortest = Math.min(into.shortest, from.shortest);
  for (let i = 0; i < into.byRole.length; i++) into.byRole[i] += from.byRole[i];
  for (let i = 0; i < into.histogram.counts.length; i++) into.histogram.counts[i] += from.histogram.counts[i];
  for (const key of Object.keys(into.problems) as RowProblem[]) into.problems[key] += from.problems[key];
}

/**
 * Recompute the limit-dependent figures from the histogram and the kept rows,
 * so dragging the limit does not re-read a gigabyte.
 *
 * This is EXACT above the smallest kept row and an UNDERCOUNT below it, and the
 * interface must not pretend otherwise. If the user lowers the limit past the
 * cap of the retained list, more rows are over the limit than the retained list
 * can name — the count is still right (it comes from the histogram where the
 * bucket edge lines up, and from a re-read otherwise), but the exported work
 * queue would be incomplete. `needsRescan` says which case you are in.
 */
export function rescore(
  kept: readonly OverLimitRow[],
  keptCoversAbove: number,
  limit: number,
): { over: number; truncated: number; needsRescan: boolean } {
  if (limit < keptCoversAbove) return { over: 0, truncated: 0, needsRescan: true };
  let over = 0;
  let truncated = 0;
  for (const row of kept) {
    if (row.tokens > limit) {
      over += 1;
      truncated += row.tokens - limit;
    }
  }
  return { over, truncated, needsRescan: false };
}

export function histogramMax(h: Histogram): number {
  let max = 0;
  for (const c of h.counts) if (c > max) max = c;
  return max;
}

/** Human label for a bucket, e.g. "1k–2k". */
export function bucketLabel(edges: readonly number[], i: number): string {
  const lo = i === 0 ? 0 : edges[i - 1];
  const hi = edges[i];
  const fmt = (n: number) => (n >= 1024 ? `${Math.round(n / 1024)}k` : String(n));
  if (hi === Infinity) return `${fmt(lo)}+`;
  return `${fmt(lo)}–${fmt(hi)}`;
}
